/**
 * <s-signup-popup> — opens the email sign-up dialog once per visitor.
 *
 * Opens the first <dialog> inside after `delay` seconds, unless the visitor
 * dismissed it within the last `snooze-days` days or already signed up.
 * Closing the dialog snoozes it; submitting the form stops it for good.
 * Server-returned feedback (success or errors) is opened by the inner
 * <s-dialog open-on-connect>, so this element skips its timer in that case.
 *
 * Attributes:
 *   delay        — seconds before opening (default 8)
 *   snooze-days  — days to wait after a dismissal (default 14)
 *   storage-key  — localStorage key (default "signup-popup")
 */
class SSignupPopup extends HTMLElement {
  #ac;
  #timer;

  connectedCallback() {
    const dialog = this.querySelector('dialog');
    if (!dialog) return;

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const key = this.getAttribute('storage-key') || 'signup-popup';

    dialog.addEventListener('close', () => this.#store(key, 'dismissed'), opts);
    this.querySelector('form')?.addEventListener('submit', () => this.#store(key, 'subscribed'), opts);

    if (this.querySelector('[open-on-connect]')) {
      this.#store(key, 'subscribed');
      return;
    }
    if (!this.#shouldOpen(key)) return;

    const delay = Number(this.getAttribute('delay') ?? 8) * 1000;
    this.#timer = setTimeout(() => {
      if (!dialog.open && !document.querySelector('dialog:modal')) dialog.showModal();
    }, delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
  }

  #shouldOpen(key) {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (!saved) return true;
      if (saved.state === 'subscribed') return false;
      const snoozeDays = Number(this.getAttribute('snooze-days') ?? 14);
      return Date.now() - saved.at > snoozeDays * 86400000;
    } catch {
      return true;
    }
  }

  #store(key, state) {
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (saved?.state === 'subscribed' && state === 'dismissed') return;
      localStorage.setItem(key, JSON.stringify({ state, at: Date.now() }));
    } catch {
      /* Storage unavailable (private mode): the popup may show again next visit. */
    }
  }
}

customElements.define('s-signup-popup', SSignupPopup);
